// P2-7 — PULL TO REFRESH IN THE INSTALLED APP.
//
// **HER FINDING: the PWA has no pull-to-refresh AND no visible manual refresh path.** In a browser
// tab there is always a reload button; an installed app has no chrome, so a member looking at a
// stale feed has nothing at all to do about it, and the app reads as broken rather than merely
// quiet. That is why this gesture is the standard one and why its absence is louder than its
// presence would be.
//
// THIS FILE IS THE LAW ONLY — the physics, when a pull is allowed to start, and THE REGISTRY. It
// imports nothing, so `scripts/p27-pull-refresh.mjs` loads and RUNS it, which is the entry
// requirement every law in `src/` is held to. The touch handling and the indicator live in
// `src/components/PullToRefresh.tsx`, which holds hooks and could not be run out here.
//
// **THE REGISTRY IS THE WHOLE DESIGN AND IT IS HER ORDER, NOT MY PREFERENCE.** Her words: this must
// not be built surface by surface, "or the capability lives on whichever screen needed it first and
// every other one silently does nothing on release, which is worse than no gesture at all." That is
// W12/R26/R35's shape stated as design law, and this house has paid for it three times: the way back
// from the Cauldron lived on one call site of eleven, the confirm-before-delete lived on the journal
// alone, and the save chip lived in the one reader that needed it first. So a surface JOINS by
// registering a refresh; the gesture asks the registry what to run and NEVER names a screen.

// ---------------------------------------------------------------------------
// THE PHYSICS.
//
// A pull that maps finger travel 1:1 feels like dragging the page off its hinges. Real ones resist:
// the further she pulls, the less it moves, so the indicator arrives at the threshold and then
// visibly refuses to go much further, which is what tells a thumb the gesture is armed.
// ---------------------------------------------------------------------------
export const PULL_THRESHOLD = 64;      // travel at which release will refresh
export const PULL_MAX = 96;            // the indicator never travels past this, however hard she pulls
export const PULL_RESISTANCE = 2.2;    // divisor on raw finger travel

// The dead zone. Under this a "pull" is the start of an ordinary scroll or a mis-touch, and treating
// it as a gesture is how a pull-to-refresh ends up fighting the page it lives on.
export const PULL_SLOP = 12;

export function pullDistance(rawDelta) {
  const raw = Number(rawDelta) || 0;
  if (raw <= 0) return 0;
  // Resistance plus a hard ceiling: asymptotic would be prettier and this is legible, which matters
  // more in a law two other files read.
  return Math.min(PULL_MAX, raw / PULL_RESISTANCE);
}

export function pullState(distance) {
  const d = Number(distance) || 0;
  if (d <= 0) return "idle";
  if (d >= PULL_THRESHOLD) return "ready";
  return "pulling";
}

export function willRefresh(distance) {
  return pullState(distance) === "ready";
}

// How far the indicator has rotated/filled, 0..1. Kept here rather than in the component so the
// number the gate checks is the number the pixel uses.
export function pullProgress(distance) {
  return Math.max(0, Math.min(1, (Number(distance) || 0) / PULL_THRESHOLD));
}

// ---------------------------------------------------------------------------
// WHEN A PULL MAY START AT ALL. **THE NEGATIVE CASE IS THE ONE THAT MATTERS** (her order says so
// outright, and names it as the check most likely to be skipped): a pull anywhere but the very top
// of the page must do NOTHING, or the gesture hijacks ordinary scrolling and the app feels like it
// is fighting her thumb.
//
// PHONE ONLY, by her order. On a desktop there is a reload button and a mouse wheel that overscrolls
// for reasons that have nothing to do with wanting fresh data.
// ---------------------------------------------------------------------------
export const PULL_MAX_WIDTH = 900;

// **AT THE TOP MEANS `<= 0`, NOT `=== 0`, AND THAT IS AN iOS FACT RATHER THAN A TOLERANCE.** When a
// member at the top of the page drags down in Safari, iOS begins its own rubber-band and the
// scroller's `scrollTop` goes NEGATIVE. A test written as `> 0` refuses the pull is fine; a test
// written as `=== 0` refuses the gesture the moment the rubber band starts, which is exactly when
// the member is pulling. Chrome does not rubber-band the document this way, so a Chrome-green run
// never sees it.
export function atTop(scrollTop) {
  return Number(scrollTop || 0) <= 0;
}

// `scrollTop` is the reading of THE ELEMENT THAT ACTUALLY SCROLLS. `scrollY` is accepted as an alias
// because the window is that element on most pages, but the caller is expected to resolve the real
// scroller and pass its position — a listener reading the window while a wrapper scrolls is one of
// the three things her trace named.
export function canStartPull({ scrollTop, scrollY = 0, width = 0, touch = true, busy = false, interactive = false } = {}) {
  if (busy) return false;                       // a refresh already running
  if (!touch) return false;                     // phone gesture, not a mouse one
  if (Number(width) > PULL_MAX_WIDTH) return false;
  if (!atTop(scrollTop === undefined ? scrollY : scrollTop)) return false;
  // A drag that begins inside something scrollable or draggable of its own is that thing's gesture,
  // not the page's — a horizontal card rail, a textarea, a map.
  if (interactive) return false;
  return true;
}

// **A REFRESH THAT RAN NOTHING IS THE SECOND FAILURE SHE DESCRIBED: the indicator turns and the
// content does not change.** It happens whenever the screen she is standing on never joined the
// registry, and it is silent — `runRefresh` returns `{ ran: 0 }` and the spinner still spins. So a
// zero-handler refresh falls back to reloading the document, which is what a pull means in an
// installed app with no browser chrome: the honest answer to "give me this screen again".
export function needsFullReload(ran) {
  return Number(ran || 0) === 0;
}

// ---------------------------------------------------------------------------
// THE REGISTRY. A surface joins; the gesture asks.
//
// Handlers are keyed so a remount replaces rather than doubling — a surface that registers twice
// would double every refresh, which is invisible until it is a bill.
// ---------------------------------------------------------------------------
const handlers = new Map();

export function registerRefresh(key, fn) {
  if (!key || typeof fn !== "function") return () => {};
  handlers.set(String(key), fn);
  return () => { if (handlers.get(String(key)) === fn) handlers.delete(String(key)); };
}

export function registeredRefreshKeys() {
  return [...handlers.keys()];
}

// EVERY REGISTERED SURFACE REFRESHES, and one that throws does not stop the others. A refresh is a
// set of independent re-fetches; letting the first rejection abort the rest would make the gesture
// unreliable in exactly the conditions it exists for (a flaky connection).
export async function runRefresh() {
  const entries = [...handlers.entries()];
  const results = await Promise.allSettled(entries.map(([, fn]) => fn()));
  return {
    ran: entries.length,
    failed: results.map((r, i) => (r.status === "rejected" ? entries[i][0] : null)).filter(Boolean),
  };
}

// ---------------------------------------------------------------------------
// P2-7c — A PULL ALSO ASKS WHETHER A NEW VERSION IS WAITING.
//
// PN1 already has both pieces: a waiting worker, and a visible Refresh affordance that is HERS to
// tap. A pull applies the update THROUGH that affordance rather than inventing a second update path
// — which would mean two things that can install a worker and one of them bypassing the notice she
// was given. The law here is only the decision; the applying is `applyUpdate` in `src/lib/pwa.ts`.
//
// **AND THE UPDATE IS APPLIED AFTER THE DATA REFRESH, NEVER INSTEAD OF IT.** Applying it reloads the
// page, so doing it first throws away the re-fetch she actually asked for and the gesture reads as a
// random reload.
export function pullOutcome({ waitingWorker = false } = {}) {
  return waitingWorker ? "refresh-then-update" : "refresh";
}

// The one sentence the indicator says. Her register: no windups, and it never claims more than it
// did — "Up to date" is a lie when a re-fetch failed.
export const PULL_COPY = {
  pulling: "Pull to refresh",
  ready: "Release to refresh",
  refreshing: "Refreshing",
  updating: "Installing the new version",
};
